import React, { useState, useEffect, useMemo } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";

export const AdminManageUsers = () => {
  const { admin } = useAdminAuthContext();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [workouts, setWorkouts] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [updatingUserId, setUpdatingUserId] = useState(null);

  // =========================
  // PAYMENT MODAL STATE
  // =========================
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentUser, setPaymentUser] = useState(null);

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [paymentNote, setPaymentNote] = useState("");

  const LIMIT = 25;

  // =========================
  // Helpers
  // =========================
  const safeDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    return isNaN(date.getTime()) ? null : date;
  };

  const isSubscriptionValid = (u) => {
    const end = safeDate(u.subscriptionEnd);
    if (!end) return false;
    return end > new Date();
  };

  const getDaysLeft = (u) => {
    const end = safeDate(u.subscriptionEnd);
    if (!end) return null;
    const diff = end.getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const hasPaidEver = (u) => {
    const logs = u.paymentLogs || [];
    return logs.length > 0 || !!u.lastPaymentDate;
  };

  const getLastPaymentDate = (u) => {
    if (u.lastPaymentDate) return safeDate(u.lastPaymentDate);

    const logs = u.paymentLogs || [];
    if (logs.length === 0) return null;

    const sorted = [...logs].sort((a, b) => {
      const ad = new Date(a.paidAt || a.createdAt || 0).getTime();
      const bd = new Date(b.paidAt || b.createdAt || 0).getTime();
      return bd - ad;
    });

    return safeDate(sorted[0]?.paidAt || sorted[0]?.createdAt);
  };

  // =========================
  // Fetch Users
  // =========================
  const fetchUsers = async (page) => {
    try {
      setIsLoading(true);
      setError("");

      const res = await fetch(
        API_URL.ADMIN_MANAGE_ALL_USERS + `?page=${page}&limit=${LIMIT}`,
        { headers: { Authorization: `Bearer ${admin.token}` } }
      );

      const data = await res.json();
      if (!res.ok || !data?.status)
        throw new Error(data?.message || "Failed to fetch users");

      setUsers(data.users || []);
      setCurrentPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // =========================
  // Fetch Workouts
  // =========================
  const fetchWorkouts = async () => {
    try {
      const res = await fetch(API_URL.ADMIN_GET_ALL_WORKOUTS, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.status)
        throw new Error(data?.message || "Failed to fetch workouts");
      setWorkouts(data.workouts || []);
    } catch (err) {
      console.log("fetchWorkouts error:", err.message);
    }
  };

  // =========================
  // Update User (generic)
  // =========================
  const updateUser = async (userId, payload) => {
    try {
      setUpdatingUserId(userId);
      setError("");

      let url = API_URL.ADMIN_UPDATE_USER + userId;

      // subscription/payment updates
      // - isActive toggle
      // - payment record (amount, method, note)
      if (
        "isActive" in payload ||
        "amount" in payload ||
        "method" in payload ||
        "note" in payload
      ) {
        url = API_URL.ADMIN_UPDATE_SUBSCRIPTION + userId;
      }

      // workout assignment updates
      if ("currentWorkoutKey" in payload) {
        url = API_URL.ADMIN_UPDATE_WORKOUT + userId;
      }

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data?.status)
        throw new Error(data?.message || "Failed to update user");

      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, ...data.user } : u))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingUserId(null);
    }
  };

  // =========================
  // Search + Filter
  // =========================
  useEffect(() => {
    let list = [...users];

    if (statusFilter === "active") list = list.filter((u) => u.isActive);
    else if (statusFilter === "inactive")
      list = list.filter((u) => !u.isActive);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((u) => {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const telegramUsername = (u.telegramUsername || "").toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          telegramUsername.includes(q)
        );
      });
    }

    setFilteredUsers(list);
  }, [users, searchQuery, statusFilter]);

  // =========================
  // Load on page change
  // =========================
  useEffect(() => {
    if (!admin?.token) return;
    fetchUsers(currentPage);
    fetchWorkouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, admin?.token]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  // =========================
  // Stats
  // =========================
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.isActive).length;
    const inactive = users.filter((u) => !u.isActive).length;
    const telegramLinked = users.filter((u) => u.telegramLinked).length;
    const paid = users.filter((u) => !!u.lastPaymentAt).length;

    return { total, active, inactive, telegramLinked, paid };
  }, [users]);

  // =========================
  // Payment Modal Handlers
  // =========================
  const openPaymentModal = (u) => {
    setPaymentUser(u);
    setPaymentAmount("");
    setPaymentMethod("upi");
    setPaymentNote("");
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentUser(null);
    setPaymentAmount("");
    setPaymentMethod("upi");
    setPaymentNote("");
  };

  const submitPayment = async () => {
    if (!paymentUser?._id) return;

    const amountNum = Number(paymentAmount);

    if (!amountNum || isNaN(amountNum) || amountNum <= 0) {
      alert("Enter a valid payment amount");
      return;
    }

    // IMPORTANT:
    // This action should ONLY:
    // - log payment
    // - set lastPaymentDate
    // - extend subscription by 1 year
    // - set isActive true (if backend does)
    await updateUser(paymentUser._id, {
      amount: amountNum,
      method: paymentMethod,
      note: paymentNote,
    });

    closePaymentModal();
  };

  return (
    <div className="flex h-screen bg-zinc-100">
      <AdminSideMenu />

      <div className="flex-1 p-6 pt-16 flex flex-col">
        {/* HEADER */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-zinc-900">Manage Users</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Leads, subscriptions, Telegram, payments and workout assignment.
          </p>
        </div>

        {/* STATS ROW */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-500">Total</div>
            <div className="mt-1 text-xl font-bold text-zinc-900">
              {stats.total}
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-500">Paid</div>
            <div className="mt-1 text-xl font-bold text-indigo-700">
              {stats.paid}
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-500">Active</div>
            <div className="mt-1 text-xl font-bold text-green-700">
              {stats.active}
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-500">Leads</div>
            <div className="mt-1 text-xl font-bold text-yellow-700">
              {stats.inactive}
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-500">Telegram Linked</div>
            <div className="mt-1 text-xl font-bold text-purple-700">
              {stats.telegramLinked}
            </div>
          </div>
        </div>

        {/* SEARCH + STATUS FILTER */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="text"
            placeholder="Search by name, email, telegram username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-[380px] rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm outline-none focus:border-zinc-400"
          />

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setStatusFilter("all")}
              className={`rounded-xl px-4 py-2 text-sm font-semibold border transition ${
                statusFilter === "all"
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-800 border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              All
            </button>

            <button
              onClick={() => setStatusFilter("active")}
              className={`rounded-xl px-4 py-2 text-sm font-semibold border transition ${
                statusFilter === "active"
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-zinc-800 border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              Active
            </button>

            <button
              onClick={() => setStatusFilter("inactive")}
              className={`rounded-xl px-4 py-2 text-sm font-semibold border transition ${
                statusFilter === "inactive"
                  ? "bg-yellow-500 text-white border-yellow-500"
                  : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              Leads
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="flex-1 overflow-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full table-auto">
            <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Workout
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Subscription
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Payment
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Access
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Telegram
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Joined
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="py-8 text-center text-sm text-zinc-500"
                  >
                    Loading users...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan="8"
                    className="py-8 text-center text-sm text-red-600"
                  >
                    {error}
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="py-8 text-center text-sm text-zinc-500"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u, index) => {
                  const isUpdating = updatingUserId === u._id;

                  const subValid = isSubscriptionValid(u);
                  const daysLeft = getDaysLeft(u);

                  const paidEver = hasPaidEver(u);
                  const lastPaidAt = getLastPaymentDate(u);

                  return (
                    <tr
                      key={u._id}
                      className={`border-b border-zinc-100 ${
                        index % 2 === 0 ? "bg-white" : "bg-zinc-50/30"
                      }`}
                    >
                      {/* User */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 overflow-hidden rounded-full border border-zinc-200 bg-zinc-100">
                            {u.picture ? (
                              <img
                                src={u.picture}
                                alt={u.name}
                                className="h-full w-full object-cover"
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs font-bold text-zinc-600">
                                {(u.name || "U")[0]}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-zinc-900">
                              {u.name || "Unnamed"}
                            </div>
                            <div className="text-xs text-zinc-500">{u.email}</div>
                            {u.telegramUsername && (
                              <div className="text-xs text-zinc-500">
                                @{u.telegramUsername}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Workout */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <select
                            value={u.currentWorkoutKey || ""}
                            disabled={isUpdating}
                            onChange={(e) =>
                              updateUser(u._id, {
                                currentWorkoutKey: e.target.value,
                              })
                            }
                            className="rounded-xl border border-zinc-200 px-3 py-2 text-xs outline-none focus:border-zinc-400"
                          >
                            <option value="">Not assigned</option>
                            {workouts.map((w) => (
                              <option key={w.key} value={w.key}>
                                {w.title}
                              </option>
                            ))}
                          </select>

                          {u.currentWorkoutStats && (
  <span
    className={`inline-flex w-fit rounded-full px-2 py-1 text-xs font-semibold ${
      u.currentWorkoutStats.completed
        ? "bg-green-100 text-green-800 border border-green-200"
        : "bg-zinc-200 text-zinc-800 border border-zinc-300"
    }`}
  >
    Step {u.currentWorkoutStats.nextStepIndex - 1} sent
    {u.currentWorkoutStats.stepsSent > 0
      ? ` / ${u.currentWorkoutStats.stepsSent}`
      : ""}
    {u.currentWorkoutStats.completed ? " ✅" : ""}
  </span>
)}
                        </div>
                      </td>

                      {/* Subscription */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          {u.subscriptionEnd ? (
                            <>
                              <div className="text-xs text-zinc-500">
                                Valid till
                              </div>
                              <div className="text-sm font-semibold text-zinc-900">
                                {new Date(u.subscriptionEnd).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )}
                              </div>

                              <div className="flex gap-2 flex-wrap">
                                {subValid ? (
                                  <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                    Valid
                                  </span>
                                ) : (
                                  <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                    Expired
                                  </span>
                                )}

                                {typeof daysLeft === "number" && (
                                  <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-zinc-100 text-zinc-800 border border-zinc-200">
                                    {daysLeft > 0
                                      ? `${daysLeft} days left`
                                      : `${Math.abs(daysLeft)} days overdue`}
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-sm text-zinc-500">
                              Not started
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Payment */}
                    {/* Payment */}
<td className="px-4 py-4">
  {u.lastPaymentAt ? (
    <div className="flex flex-col gap-1">
      <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-green-100 text-green-800">
        Paid
      </span>
      <span className="text-[11px] text-zinc-500">
        {new Date(u.lastPaymentAt).toLocaleDateString("en-IN")}
      </span>
    </div>
  ) : (
    <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800">
      Unpaid
    </span>
  )}
</td>


                      {/* Access */}
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            u.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-zinc-200 text-zinc-800"
                          }`}
                        >
                          {u.isActive ? "Enabled" : "Disabled"}
                        </span>
                      </td>

                      {/* Telegram */}
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            u.telegramLinked
                              ? "bg-indigo-100 text-indigo-800"
                              : "bg-zinc-200 text-zinc-800"
                          }`}
                        >
                          {u.telegramLinked ? "Linked" : "Not linked"}
                        </span>
                      </td>

                      {/* Joined */}
                      <td className="px-4 py-4 text-sm text-zinc-700">
                        {u.createdAt
                          ? new Date(u.createdAt).toLocaleDateString("en-IN")
                          : "-"}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          {/* Record Payment */}
                          <button
                            disabled={isUpdating}
                            onClick={() => openPaymentModal(u)}
                            className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                              isUpdating
                                ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                          >
                            💰 Record Payment
                          </button>

                          {/* Enable Access */}
                          <button
                            disabled={isUpdating || u.isActive}
                            onClick={() => updateUser(u._id, { isActive: true })}
                            className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                              isUpdating || u.isActive
                                ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                                : "bg-indigo-600 text-white hover:bg-indigo-700"
                            }`}
                          >
                            Enable
                          </button>

                          {/* Disable Access */}
                          <button
                            disabled={isUpdating || !u.isActive}
                            onClick={() =>
                              updateUser(u._id, { isActive: false })
                            }
                            className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                              isUpdating || !u.isActive
                                ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                                : "bg-zinc-900 text-white hover:bg-zinc-800"
                            }`}
                          >
                            Disable
                          </button>
                        </div>

                        <div className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                          <span className="font-semibold">Record Payment</span>{" "}
                          logs payment + extends subscription by 1 year.{" "}
                          Enable/Disable does not change subscription.
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              currentPage === 1 || isLoading
                ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
          >
            Previous
          </button>

          <div className="text-sm text-zinc-600">
            Page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              currentPage === totalPages || isLoading
                ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* =========================
          PAYMENT MODAL
      ========================== */}
      {paymentModalOpen && paymentUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-zinc-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-bold text-zinc-900">
                  Record Payment
                </div>
                <div className="mt-1 text-sm text-zinc-600">
                  This will log payment and extend subscription by{" "}
                  <b>1 year</b>.
                </div>

                <div className="mt-3 text-sm text-zinc-700">
                  <b>User:</b> {paymentUser.name} <br />
                  <b>Email:</b> {paymentUser.email}
                </div>
              </div>

              <button
                onClick={closePaymentModal}
                className="rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
              >
                ✕
              </button>
            </div>

            {/* Amount */}
            <div className="mt-5">
              <label className="text-sm font-semibold text-zinc-900">
                Amount (₹)
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="1499"
                className="mt-2 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>

            {/* Method */}
            <div className="mt-4">
              <label className="text-sm font-semibold text-zinc-900">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="upi">UPI</option>
                <option value="gpay">Google Pay</option>
                <option value="phonepe">PhonePe</option>
                <option value="paytm">Paytm</option>
                <option value="bank">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Note */}
            <div className="mt-4">
              <label className="text-sm font-semibold text-zinc-900">
                Note (optional)
              </label>
              <textarea
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="Example: UPI ref 1234, paid on 15 Feb"
                rows={3}
                className="mt-2 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>

            {/* Buttons */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={closePaymentModal}
                className="w-1/2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
              >
                Cancel
              </button>

              <button
                onClick={submitPayment}
                className="w-1/2 rounded-2xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                ✅ Confirm Payment
              </button>
            </div>

            <div className="mt-4 text-xs text-zinc-500 leading-relaxed">
              ⚠️ Confirming payment will extend subscription by 1 year from
              current end date (if still valid) or from today (if expired).
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
